/// <reference types="@types/google.maps" />

import { AdvancedMarker, type ColorScheme, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'tailwindcss/colors';

import { type MappedAssetSensor, sensorStatusLabels } from '@/common/asset-sensor';
import { type Theme, useTheme } from '@/context/ThemeContext';

export interface SensorMapProps {
	sensors: MappedAssetSensor[];
	selectedSensor: MappedAssetSensor | null;
	onSelectSensor: (sensor: MappedAssetSensor) => void;
}

const googleThemeMapping: Record<Theme, ColorScheme> = {
	light: 'LIGHT',
	dark: 'DARK',
	system: 'FOLLOW_SYSTEM',
};

// Create custom marker icon HTML
const MarkerIconHtml = ({ sensor, isSelected }: { sensor: MappedAssetSensor; isSelected: boolean }) => {
	const { t } = useTranslation();
	const color = isSelected ? colors.blue[600] : sensor.statusSettings.map.iconColor;
	return (
		<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
			<title>{t('health.sensors.markerTitle', { name: sensor.name })}</title>
			<path
				d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.125 12.5 28.125S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z"
				fill={color}
				stroke={isSelected ? '#1E40AF' : '#000'}
				strokeWidth={isSelected ? '2' : '1'}
			/>
			<circle cx="12.5" cy="12.5" r="6" fill="white" />
		</svg>
	);
};

export const SensorGoogleMap: React.FC<SensorMapProps> = ({ sensors, selectedSensor, onSelectSensor }) => {
	const { t } = useTranslation();
	const googleMap = useMap();
	const { theme } = useTheme();
	const statusLabels = useMemo(() => sensorStatusLabels(t), [t]);
	useEffect(() => {
		if (!googleMap || !!googleMap.getBounds()) {
			return;
		}
		if (!sensors?.length) {
			return;
		}
		const coordinates = sensors.map((sensor) => sensor.coordinates).filter((coord) => !!coord);
		// Fit map to sensor bounds
		googleMap.fitBounds(
			new google.maps.LatLngBounds(
				new google.maps.LatLng(
					Math.min(...coordinates.map((coordinate) => coordinate.lat)),
					Math.min(...coordinates.map((coordinate) => coordinate.lng)),
				),
				new google.maps.LatLng(
					Math.max(...coordinates.map((coordinate) => coordinate.lat)),
					Math.max(...coordinates.map((coordinate) => coordinate.lng)),
				),
			),
		);
	}, [googleMap, sensors]);
	const onMarkerClick = (sensor: MappedAssetSensor) => {
		onSelectSensor(sensor);
	};
	return (
		<div className="h-full w-full overflow-hidden border-2">
			<GoogleMap
				className="h-full w-full"
				gestureHandling="greedy"
				defaultCenter={{ lat: 0, lng: 0 }}
				defaultZoom={2}
				mapId="DEMO_MAP_ID"
				colorScheme={googleThemeMapping[theme]}
			>
				{sensors.map((sensor) => (
					<AdvancedMarker
						key={sensor.uniqId}
						position={sensor.coordinates}
						title={sensor.label}
						zIndex={selectedSensor?.uniqId === sensor.uniqId ? 1000 : 20}
					>
						<div className="flex flex-col items-center">
							<div
								className="bg-accent px-2 py-1 rounded-md text-xs font-medium shadow-lg border"
								style={{ minWidth: 'max-content', borderColor: sensor.statusSettings.map.borderColor }}
							>
								<span className="mr-1">{sensor.label}</span>
								<span className={`font-semibold`} style={{ color: sensor.statusSettings.map.statusColor }}>
									({statusLabels[sensor.status]})
								</span>
							</div>
							{/** biome-ignore lint/a11y/useKeyWithClickEvents: need for click */}
							{/** biome-ignore lint/a11y/noStaticElementInteractions: need for click */}
							<span onClick={() => onMarkerClick(sensor)} className="cursor-pointer">
								<MarkerIconHtml sensor={sensor} isSelected={selectedSensor?.uniqId === sensor.uniqId} />
							</span>
						</div>
					</AdvancedMarker>
				))}
			</GoogleMap>
		</div>
	);
};
